import type { CaptureResult, CapturedNetworkRequest } from 'posthog-js'

/**
 * Keeps the credentials the dashboard carries in its own URLs out of PostHog.
 * Emailed links put a live credential in the address: a password-reset or
 * sign-in JWT, a teammate or owner invite, an SSO session or 2FA token, an
 * OAuth code or state. posthog-js copies the address into nearly every event
 * (`$current_url`, `$pathname`, `$referrer`, `$session_entry_*`,
 * `$prev_pageview_*`, the `$initial_*` person properties, autocapture
 * `href`s), and anyone with access to the project can read them for as long
 * as the events are kept. The secret is replaced and the route shape kept, so
 * `/password-reset/:token` still groups as one page.
 */

export const TOKEN_PATH_ROUTES = [
  'password-reset',
  'signin-link',
  'inviteowner',
  'newteammate',
]

export const TOKEN_PLACEHOLDER = ':token'
export const REDACTED_VALUE = '[redacted]'

// A URL nested in a query value (`/login?redirect=…`, which can itself sit in
// another `redirect`) is percent-encoded once per level, so each delimiter is
// matched raw or at any encoding depth: `/`, `%2F`, `%252F`, ….
const encoded = (hex: string) => `%(?:25)*${hex}`
const SLASH = `(?:/|${encoded('2F')})`
const QUESTION = `(?:\\?|${encoded('3F')})`
const AMPERSAND = `(?:&|${encoded('26')})`
const HASH = `(?:#|${encoded('23')})`
const EQUALS = `(?:=|${encoded('3D')})`

// A value runs to the next delimiter of any depth. A percent escape that is
// not one of those delimiters belongs to the value.
const valueUntil = (delimiterHex: string[]) =>
  `(?:[^/?#&\\s"'<>%]|%(?!(?:25)*(?:${delimiterHex.join('|')}))[0-9A-Fa-f]{2})+`

const TOKEN_PATH_SEGMENT = new RegExp(
  `(${SLASH}(?:${TOKEN_PATH_ROUTES.join('|')})${SLASH})${valueUntil(['2F', '3F', '23', '26'])}`,
  'gi',
)

const SENSITIVE_PARAM_NAME =
  '[\\w.-]*token|code|state|email|[\\w.-]*(?:secret|password|signature|credential)[\\w.-]*'

const SENSITIVE_QUERY_VALUE = new RegExp(
  `((?:${QUESTION}|${AMPERSAND}|${HASH})(?:${SENSITIVE_PARAM_NAME})${EQUALS})${valueUntil(['26', '23'])}`,
  'gi',
)

const JWT = /eyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]*/g

export function scrubSensitiveUrlText(text: string): string {
  return text
    .replace(TOKEN_PATH_SEGMENT, `$1${TOKEN_PLACEHOLDER}`)
    .replace(SENSITIVE_QUERY_VALUE, `$1${REDACTED_VALUE}`)
    .replace(JWT, REDACTED_VALUE)
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false
  const proto: unknown = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function scrubDeep(value: unknown): unknown {
  if (typeof value === 'string') return scrubSensitiveUrlText(value)
  if (Array.isArray(value)) return value.map(scrubDeep)
  if (!isPlainObject(value)) return value
  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => [
      scrubSensitiveUrlText(key),
      scrubDeep(inner),
    ]),
  )
}

export function scrubSensitiveEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event) return event
  const { $snapshot_data: snapshotData, ...rest } = event.properties
  const properties = scrubDeep(rest) as CaptureResult['properties']
  return {
    ...event,
    properties:
      snapshotData === undefined
        ? properties
        : { ...properties, $snapshot_data: snapshotData },
    ...(event.$set
      ? { $set: scrubDeep(event.$set) as CaptureResult['properties'] }
      : {}),
    ...(event.$set_once
      ? { $set_once: scrubDeep(event.$set_once) as CaptureResult['properties'] }
      : {}),
  }
}

/**
 * Wraps a `session_recording.maskCapturedNetworkRequestFn`. posthog-js also
 * routes the replay's page URLs through that hook as `{ name: url }` (the
 * recording's meta `href`, `$url_changed`, its `$pageview` markers), so this
 * covers them and the document's navigation request as well as fetches.
 */
export function scrubReplayUrl(
  mask: (request: CapturedNetworkRequest) => CapturedNetworkRequest,
): (request: CapturedNetworkRequest) => CapturedNetworkRequest {
  return (request) => {
    const masked = mask(request)
    return typeof masked.name === 'string'
      ? { ...masked, name: scrubSensitiveUrlText(masked.name) }
      : masked
  }
}

const INITIAL_PERSON_INFO = '$initial_person_info'

type InitialPersonInfo = { r?: unknown; u?: unknown }

type PersonInfoStore = {
  props: Record<string, unknown>
  register: (props: Record<string, unknown>) => boolean
}

/**
 * posthog-js stores the first URL and referrer it ever saw (`u` / `r` under
 * `$initial_person_info`) and sends them as `$initial_*` person properties
 * with every `/flags` request, which `before_send` never sees. Rewrites the
 * stored pair in place; a no-op once it is clean. Call it before the first
 * `/flags` request (posthog-js's `loaded` hook) for a pair stored on an
 * earlier visit, and after each capture for one stored by this visit's first.
 */
export function scrubStoredInitialPersonInfo(
  persistence: PersonInfoStore | undefined,
): void {
  const info = persistence?.props[INITIAL_PERSON_INFO]
  if (!persistence || !isPlainObject(info)) return
  const { r, u }: InitialPersonInfo = info
  const scrubbed = {
    ...info,
    ...(typeof r === 'string' ? { r: scrubSensitiveUrlText(r) } : {}),
    ...(typeof u === 'string' ? { u: scrubSensitiveUrlText(u) } : {}),
  }
  if (scrubbed.r !== r || scrubbed.u !== u) {
    persistence.register({ [INITIAL_PERSON_INFO]: scrubbed })
  }
}
